import React, { useEffect } from 'react';
import { Modal, typographyStyles, COLORS } from '@livecommerce/design-system';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useModal } from 'hooks';
import usePermissions from 'hooks/usePermissions.jsx';
import { PERMISSIONS } from 'shared-permissions';
import { isStage } from 'constants/gcp';
import config from '../../../../client-config.js';
import { styled } from '@linaria/react';

const { getBodyText } = typographyStyles;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.div`
  ${getBodyText(14, 500)};
  color: ${COLORS.dark_700};
`;

const Text = styled.div`
  ${getBodyText(12)};
  color: ${COLORS.dark_300};
  margin-bottom: 8px;
`;

// We use explicit styles here instead of the Button component from design-system
// to avoid hover styling to skip the user from needing to tap twice on mobile device.
const MobileButton = styled.a`
    color: #efefef;
    background-color: #e95d11;
    border-color: #e95d11;
    font-size: 12px;
    padding: 7px 16px;
    font-size: 14px;
    text-transform: uppercase;
    cursor: pointer;
    border-style: solid;
    border-width: 1px;
    border-radius: 8px;
    justify-content: center;
    align-items: center;
    margin: 0;
    font-family: Haas Grot, Helvetica, Arial, Verdana, Tahoma, sans-serif;
    font-weight: 500;
    line-height: 24px;
    -webkit-text-decoration: none;
    text-decoration: none;
    display: inline-flex;
    position: relative;
`;


export default () => {
  const { t } = useLocalization();
  const {
    isOpen,
    open,
    close,
  } = useModal();
  const { hasPermission } = usePermissions();
  const [wasOpened, setWasOpened] = React.useState(false);
  const canGoLive = hasPermission(PERMISSIONS.GO_LIVE_IN_APP);
  const canUploadVideos = hasPermission(PERMISSIONS.VOD_MANAGE);
  const canUseAgentTool = hasPermission(PERMISSIONS.CALLS_AGENT_TOOL);
  const canUseChatApp = hasPermission(PERMISSIONS.CHAT_APP);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  useEffect(() => {
    if (wasOpened) return;
    if (!isMobile) return;
    if (!canGoLive && !canUploadVideos && !canUseAgentTool && !canUseChatApp) return;
    setWasOpened(true);
    open();
  }, [wasOpened, isMobile, canGoLive, canUploadVideos, canUseAgentTool, canUseChatApp, open]);

  return (
    <Modal
      name="mobile-app-modal"
      isModalOpen={isOpen}
      onClose={close}
      title={t('mobileAppModal.title')}
      overlayStyle={{ zIndex: Number.MAX_SAFE_INTEGER }}
    >
      <Content>
        {(canGoLive || canUploadVideos) && (
          <div>
            <Title>{t('genericLabels.studioApp')}</Title>
            <Text>{t('genericLabels.studioAppTagline')}</Text>
            <MobileButton href={`${config.clientAPIBaseUrl}/applink/${isStage ? 'stage' : 'prod'}-live`} target="_blank" rel="noreferrer">{t('mobileAppModal.goToAppButton')}</MobileButton>
          </div>
        )}
        {canUseAgentTool && (
          <div>
            <Title>{t('genericLabels.callsApp')}</Title>
            <Text>{t('genericLabels.callsAppTagline')}</Text>
            <MobileButton href={`${config.clientAPIBaseUrl}/applink/${isStage ? 'stage' : 'prod'}-calls`} target="_blank" rel="noreferrer">{t('mobileAppModal.goToAppButton')}</MobileButton>
          </div>
        )}
        {canUseChatApp && (
          <div>
            <Title>{t('genericLabels.chatApp')}</Title>
            <Text>{t('genericLabels.chatAppTagline')}</Text>
            <MobileButton href={`${config.clientAPIBaseUrl}/applink/${isStage ? 'stage' : 'prod'}-chat`} target="_blank" rel="noreferrer">{t('mobileAppModal.goToAppButton')}</MobileButton>
          </div>
        )}
      </Content>
    </Modal>
  );
};
